import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { shareFileToAOS } from "@/nativebridge/JsToNative";

/**
 * @returns {boolean} Android WebView / 브라우저 여부 (네이티브 공유 분기)
 */
export function isAndroidWebView() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android/i.test(userAgent);
}

/**
 * FileShareDrawer와 동일: 삼성 UA에서는 PDF 공유 옵션을 숨김.
 */
export function isPdfShareSupportedInUserAgent() {
  const ua = navigator.userAgent.toLowerCase();
  return !ua.includes("samsung");
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function cropCanvas(sourceCanvas, startX, startY, width, height, fileType) {
  const targetCanvas = document.createElement("canvas");
  targetCanvas.width = width;
  targetCanvas.height = height;
  const context = targetCanvas.getContext("2d");
  context.drawImage(sourceCanvas, startX, startY, width, height, 0, 0, width, height);
  return targetCanvas.toDataURL(`image/${fileType}`);
}

function padCanvas(sourceCanvas, paddingPx, backgroundColor = "#ffffff") {
  const pad = Math.max(0, Math.floor(paddingPx || 0));
  if (!pad) return sourceCanvas;
  const targetCanvas = document.createElement("canvas");
  targetCanvas.width = sourceCanvas.width + pad * 2;
  targetCanvas.height = sourceCanvas.height + pad * 2;
  const context = targetCanvas.getContext("2d");
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, targetCanvas.width, targetCanvas.height);
  context.drawImage(sourceCanvas, pad, pad);
  return targetCanvas;
}

/**
 * 지정한 DOM 노드를 캡처한 뒤 JPG/PNG/PDF로 OS 공유 시트(또는 Android 네이티브 공유)를 호출합니다.
 * 리포트 FileShareDrawer.generateImage + ReportWeekly 공유 분기와 동일한 규칙을 따릅니다.
 *
 * @param {HTMLElement|null} element 캡처할 루트 요소
 * @param {'jpeg'|'png'|'pdf'} fileType ShareFileTypeDialog value와 동일
 * @param {string} baseFileName 확장자 제외 (예: pokoro_conversation_123)
 * @returns {Promise<void>}
 */
export async function captureElementAndShare(element, fileType, baseFileName) {
  if (!element) {
    console.warn("captureElementAndShare: element is null");
    return;
  }

  const captureScale = Math.min(2, window.devicePixelRatio || 1);
  const baseCanvas = await html2canvas(element, {
    backgroundColor: "#ffffff",
    useCORS: true,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    scale: captureScale,
  });
  // PDF만 소폭 안전 여백을 추가해 경계 클리핑을 방지합니다.
  const canvas =
    fileType === "pdf"
      ? padCanvas(baseCanvas, Math.max(12, Math.round(baseCanvas.width * 0.015)))
      : baseCanvas;
  // PDF도 동일 캔버스를 PNG로 넣어 우측 경계 손실을 줄입니다.
  const imageMime = fileType === "pdf" ? "png" : fileType;
  const imageData = cropCanvas(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    imageMime
  );

  let shareData;
  let blob;

  if (fileType !== "pdf") {
    blob = await fetch(imageData).then((res) => res.blob());
    shareData = {
      files: [
        new File([blob], `${baseFileName}.${fileType}`, {
          type: `image/${fileType}`,
        }),
      ],
    };
  } else {
    // PDF만 잘리는 이슈는 px 단위/경계 반올림 오차가 원인일 수 있어
    // mm 단위 페이지 + 충분한 안전 여백으로 배치합니다.
    const mmPerPx = 25.4 / 96;
    const marginMm = 2.5;
    const contentWidthMm = canvas.width * mmPerPx;
    const contentHeightMm = canvas.height * mmPerPx;
    const pageWidthMm = contentWidthMm + marginMm * 2;
    const pageHeightMm = contentHeightMm + marginMm * 2;

    const doc = new jsPDF({
      orientation: pageWidthMm > pageHeightMm ? "l" : "p",
      unit: "mm",
      format: [pageWidthMm, pageHeightMm],
    });
    doc.addImage(imageData, "PNG", marginMm, marginMm, contentWidthMm, contentHeightMm);
    blob = doc.output("blob");
    shareData = {
      files: [new File([blob], `${baseFileName}.pdf`, { type: "application/pdf" })],
    };
  }

  if (isAndroidWebView()) {
    let type = 0;
    if (fileType === "png") type = 1;
    if (fileType === "pdf") type = 2;
    const ext = fileType === "pdf" ? "pdf" : fileType;
    const fileName = `${baseFileName}.${ext}`;
    const base64 = await blobToBase64(blob);
    shareFileToAOS(type, fileName, base64);
    return;
  }

  if (!navigator.share) {
    console.warn("captureElementAndShare: navigator.share is not available");
    return;
  }

  if (navigator.canShare && !navigator.canShare(shareData)) {
    console.warn("captureElementAndShare: cannot share this payload");
    return;
  }

  try {
    await navigator.share(shareData);
  } catch (error) {
    if (error && error.name === "AbortError") return;
    console.error("captureElementAndShare:", error);
    throw error;
  }
}
